import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAppStore } from '@/store';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { toast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import LoadingScreen from '@/components/ui/loading-screen';
import ChatGrid from '@/components/chat/ChatGrid';
import EmptyChatsState from '@/components/chat/EmptyChatsState';
import ProjectHeader from '@/components/projects/ProjectHeader';
import ProjectAiContextEditor from '@/components/projects/ProjectAiContextEditor';
import { useKnowledgeBaseDocuments } from '@/hooks/useKnowledgeBaseDocuments';
import { supabase } from '@/integrations/supabase/client';
import { disableProjectOSAccess, enableProjectOSAccess, isProjectOSAccessEnabled } from '@/lib/osIntegration';
import { FileText as DocIcon, Info, Upload } from 'lucide-react';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/build/pdf';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.js?url';

interface AIProjectDetailProps {
  projectId: string;
  onOpenChat?: (chatId: string, title?: string) => void;
  onOpenDocument?: (title: string, filePath: string, initialContent?: string) => void;
}

const AIProjectDetail = ({ projectId, onOpenChat, onOpenDocument }: AIProjectDetailProps) => {
  const { projects, chats, addChat, models, updateProject } = useSupabaseStore();
  const { user } = useAuth();
  const { user: appUser } = useAppStore();
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [osAccessEnabled, setOsAccessEnabled] = useState(false);
  const [osAccessLoading, setOsAccessLoading] = useState(false);
  const [projectDocs, setProjectDocs] = useState<Array<{ id: string; document_title: string; status: string; updated_at: string; file_path: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [kbId, setKbId] = useState<string | null>(null);
  const [kbName, setKbName] = useState('');
  const [kbDocs, setKbDocs] = useState<any[]>([]);
  const { fetchKnowledgeBaseDocuments, addDocument } = useKnowledgeBaseDocuments();

  GlobalWorkerOptions.workerSrc = pdfWorker;

  const project = useMemo(() => projects.find((item) => item.id === projectId), [projectId, projects]);

  const projectChats = useMemo(() => {
    return chats
      .filter((chat) => chat.project_id === projectId)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [chats, projectId]);

  const defaultModel = useMemo(() => {
    if (appUser?.defaultModelApiName) {
      const found = models.find((model) => model.apiName === appUser.defaultModelApiName);
      if (found) {
        return found;
      }
    }

    return models.find((model) => model.tier === 'free') || models[0];
  }, [appUser, models]);

  useEffect(() => {
    const init = async () => {
      if (!project) return;

      try {
        const enabled = await isProjectOSAccessEnabled(project.id, project.name);
        setOsAccessEnabled(enabled);
      } catch (error) {
        console.warn('Failed to check project OS access:', error);
      }
    };

    init();
  }, [project]);

  useEffect(() => {
    const fetchDocs = async () => {
      if (!projectId || !user) return;

      const { data, error } = await (supabase as any)
        .from('mapleword_project_documents')
        .select('id, document_title, status, updated_at, file_path')
        .eq('user_id', user.id)
        .eq('project_id', projectId)
        .order('updated_at', { ascending: false });

      if (!error && Array.isArray(data)) {
        setProjectDocs(data as Array<{ id: string; document_title: string; status: string; updated_at: string; file_path: string }>);
      }
    };

    fetchDocs();
  }, [projectId, user]);

  useEffect(() => {
    if (!projectId || !user) return;

    const sortDocs = (docs: typeof projectDocs) =>
      [...docs].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

    const channel = supabase
      .channel(`mp-docs-realtime-${projectId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'mapleword_project_documents', filter: `project_id=eq.${projectId}` },
        (payload) => {
          const { eventType, new: newRow, old: oldRow } = payload as any;
          const belongsToUser = (row: any) => row && row.user_id === user.id;

          if (eventType === 'INSERT' && newRow && belongsToUser(newRow)) {
            const nextDoc = {
              id: newRow.id,
              document_title: newRow.document_title,
              status: newRow.status,
              updated_at: newRow.updated_at,
              file_path: newRow.file_path,
            };

            setProjectDocs((previous) => {
              const exists = previous.some((doc) => doc.id === nextDoc.id);
              return sortDocs(exists ? previous.map((doc) => (doc.id === nextDoc.id ? nextDoc : doc)) : [nextDoc, ...previous]);
            });
          }

          if (eventType === 'UPDATE' && newRow && belongsToUser(newRow)) {
            const nextDoc = {
              id: newRow.id,
              document_title: newRow.document_title,
              status: newRow.status,
              updated_at: newRow.updated_at,
              file_path: newRow.file_path,
            };

            setProjectDocs((previous) => sortDocs(previous.map((doc) => (doc.id === nextDoc.id ? nextDoc : doc))));
          }

          if (eventType === 'DELETE' && oldRow && belongsToUser(oldRow)) {
            setProjectDocs((previous) => previous.filter((doc) => doc.id !== oldRow.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectDocs, projectId, user]);

  const ensureProjectKnowledgeBaseId = useCallback(async () => {
    if (!user || !project) return null;

    const idBasedName = `Project ${projectId} KB`;

    const { data: existingKb } = await supabase
      .from('knowledge_bases')
      .select('id, name')
      .eq('created_by', user.id)
      .eq('name', idBasedName)
      .maybeSingle();

    if (existingKb?.id) {
      return existingKb.id;
    }

    const legacyName = `Project ${project.name} KB`;
    const { data: legacyKb } = await supabase
      .from('knowledge_bases')
      .select('id, name')
      .eq('created_by', user.id)
      .eq('name', legacyName)
      .maybeSingle();

    if (legacyKb?.id) {
      await supabase
        .from('knowledge_bases')
        .update({ name: idBasedName, updated_at: new Date().toISOString() })
        .eq('id', legacyKb.id);

      return legacyKb.id;
    }

    const { data: newKb, error } = await supabase
      .from('knowledge_bases')
      .insert({
        name: idBasedName,
        description: `Knowledge base for project ${project.name}`,
        organization_id: null,
        created_by: user.id,
        is_private: true,
      })
      .select('id, name')
      .single();

    if (error) {
      return null;
    }

    return newKb?.id ?? null;
  }, [project, projectId, user]);

  useEffect(() => {
    const initKb = async () => {
      if (!projectId || !user || !project) return;

      const nextKbId = await ensureProjectKnowledgeBaseId();
      if (!nextKbId) return;

      setKbId(nextKbId);
      setKbName(`Project ${project.name} KB`);
      const docs = await fetchKnowledgeBaseDocuments(nextKbId);
      setKbDocs(docs);
    };

    initKb();
  }, [ensureProjectKnowledgeBaseId, fetchKnowledgeBaseDocuments, project, projectId, user]);

  useEffect(() => {
    if (!kbId) return;

    const channel = supabase
      .channel(`kb-docs-${kbId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'knowledge_base_documents', filter: `knowledge_base_id=eq.${kbId}` },
        async () => {
          const docs = await fetchKnowledgeBaseDocuments(kbId);
          setKbDocs(docs);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchKnowledgeBaseDocuments, kbId]);

  const openDocument = useCallback(async (title: string, filePath: string) => {
    if (!filePath) {
      toast({
        title: 'Unable to open document',
        description: 'This document is missing a file path.',
        variant: 'destructive',
      });
      return;
    }

    let content: string | undefined;

    if (user?.id) {
      const { data } = await (supabase as any)
        .from('os_user_files')
        .select('content')
        .eq('user_id', user.id)
        .eq('file_path', filePath)
        .maybeSingle();

      if (typeof data?.content === 'string') {
        content = data.content;
      }
    }

    onOpenDocument?.(title, filePath, content);
  }, [onOpenDocument, user?.id]);

  const handleNewChat = useCallback(async () => {
    if (!user || !defaultModel || !projectId || isCreatingChat) {
      if (!defaultModel) {
        toast({
          title: 'No AI Model Available',
          description: 'Cannot create a new chat because no AI models are configured.',
          variant: 'destructive',
        });
      }
      return;
    }

    setIsCreatingChat(true);

    try {
      const newChat = await addChat(user.id, 'New Chat', defaultModel.id, projectId);
      if (!newChat?.id) {
        throw new Error('Failed to create chat');
      }

      onOpenChat?.(newChat.id, newChat.title);
    } catch (error) {
      console.error('Error creating new chat in project:', error);
      toast({
        title: 'Error',
        description: 'Failed to create a new chat. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingChat(false);
    }
  }, [addChat, defaultModel, isCreatingChat, onOpenChat, projectId, user]);

  const handleToggleOSAccess = async (value: boolean) => {
    if (!project) return;

    setOsAccessLoading(true);

    try {
      if (value) {
        await enableProjectOSAccess({ id: project.id, name: project.name, description: project.description });
        await updateProject(project.id, { os_access_enabled: true });
        setOsAccessEnabled(true);
        toast({ title: 'OS Access enabled', description: 'This project will appear under Documents/Projects in the OS.' });
      } else {
        await disableProjectOSAccess({ id: project.id, name: project.name });
        await updateProject(project.id, { os_access_enabled: false });
        setOsAccessEnabled(false);
        toast({ title: 'OS Access disabled', description: 'The OS access marker was removed.' });
      }
    } catch (error: any) {
      toast({
        title: 'OS Access error',
        description: error?.message || 'Failed to toggle OS access.',
        variant: 'destructive',
      });
    } finally {
      setOsAccessLoading(false);
    }
  };

  const handleSaveAiContext = useCallback(async (nextValue: string | null) => {
    if (!project) return;

    await updateProject(project.id, {
      ai_context_markdown: nextValue,
      updated_at: new Date().toISOString(),
    });

    toast({
      title: 'AI context saved',
      description: 'Hidden markdown context is now available to chats in this project.',
    });
  }, [project, updateProject]);

  const stripHtmlTags = (value: string) => value.replace(/<\/?[^>]+>/g, '');

  const extractTextFromPdf = async (arrayBuffer: ArrayBuffer) => {
    const pdf = await getDocument({ data: arrayBuffer }).promise;
    let text = '';

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      const strings = content.items.map((item: any) => item.str || '').filter(Boolean);
      text += `${strings.join(' ')}\n`;
    }

    return text.trim();
  };

  const extractTextFromDocx = async (arrayBuffer: ArrayBuffer) => {
    const mammoth = await import('mammoth');
    const result = await mammoth.convertToHtml({ arrayBuffer });
    return stripHtmlTags((result.value || '').trim());
  };

  const handleUploadFilesToKB = async (files: FileList | null) => {
    if (!files || files.length === 0 || !kbId) return;

    setUploading(true);
    setUploadProgress('Preparing…');

    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const type = (file.type || '').toLowerCase();
        const name = file.name;
        const size = file.size;

        setUploadProgress(`Processing ${name} (${index + 1}/${files.length})…`);

        let contentText = '';

        if (type.startsWith('text/') || name.toLowerCase().endsWith('.md') || name.toLowerCase().endsWith('.csv')) {
          contentText = await file.text();
        } else if (type === 'text/html') {
          contentText = stripHtmlTags(await file.text());
        } else if (type === 'application/pdf') {
          contentText = await extractTextFromPdf(await file.arrayBuffer());
        } else if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
          contentText = await extractTextFromDocx(await file.arrayBuffer());
        }

        await addDocument(kbId, {
          name,
          content: contentText,
          fileType: type || 'application/octet-stream',
          size,
        });
      }

      const docs = await fetchKnowledgeBaseDocuments(kbId);
      setKbDocs(docs);
      setUploadProgress('Done.');
      toast({ title: 'Upload complete', description: 'Files were added to the project Knowledge Base.' });
    } catch (error: any) {
      console.error('[AIProjectDetail] KB upload error:', error);
      toast({
        title: 'Upload failed',
        description: error?.message || 'Could not upload files.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center bg-background p-6">
        <p className="text-sm text-muted-foreground">Sign in to view your AI projects in MapleOS.</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex h-full items-center justify-center bg-background p-6">
        <p className="text-sm text-muted-foreground">This project no longer exists.</p>
      </div>
    );
  }

  if (models.length === 0) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="mx-auto flex min-h-full max-w-7xl flex-col gap-8 p-6">
        <div className="flex items-center gap-2">
          <span className="text-sm">Enable OS Access</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  When ON, this project appears inside MapleOS Documents/Projects and files can be mirrored for easy access.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Switch checked={osAccessEnabled} onCheckedChange={handleToggleOSAccess} disabled={osAccessLoading} />
        </div>

        <ProjectHeader project={project} onNewChat={handleNewChat} isCreatingChat={isCreatingChat} />

        <ProjectAiContextEditor
          projectName={project.name}
          value={project.ai_context_markdown}
          onSave={handleSaveAiContext}
        />

        <section className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold">Chats</h2>
            <p className="text-sm text-muted-foreground">All chats linked to this AI project.</p>
          </div>

          {projectChats.length > 0 ? (
            <ChatGrid chats={projectChats} onOpenChat={onOpenChat} />
          ) : (
            <EmptyChatsState
              title="No chats in this project yet"
              description="Get started by creating a new chat."
              showButton
              onNewChat={handleNewChat}
            />
          )}
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-xl font-semibold">Knowledge Base</h2>
            <p className="text-sm text-muted-foreground">Upload reference material for this project.</p>
          </div>

          <Card className="p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="font-medium">{kbName || 'Project Knowledge Base'}</div>
                <div className="text-xs text-muted-foreground">
                  {kbDocs.length} {kbDocs.length === 1 ? 'document' : 'documents'}
                </div>
              </div>

              <div className="flex flex-col gap-3 lg:items-end">
                <Input
                  type="file"
                  multiple
                  accept=".txt,.md,.html,.csv,.pdf,.docx,.pptx,image/*"
                  onChange={(event) => handleUploadFilesToKB(event.target.files)}
                  className="max-w-sm"
                />
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" disabled>
                    <Upload className="mr-1 h-4 w-4" />
                    Upload
                  </Button>
                  {uploading && <div className="text-xs text-muted-foreground">{uploadProgress}</div>}
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {kbDocs.length === 0 ? (
                <div className="text-sm text-muted-foreground">No documents in this knowledge base yet.</div>
              ) : (
                kbDocs.map((doc: any) => (
                  <div key={doc.id} className="flex items-center justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-2">
                      <DocIcon className="h-4 w-4 text-muted-foreground" />
                      <div className="truncate">{doc.name}</div>
                    </div>
                    <div className="shrink-0 text-xs text-muted-foreground">
                      Updated: {new Date(doc.updated_at).toLocaleString()}
                    </div>
                  </div>
                ))
              )}
            </div>

            <p className="mt-3 text-xs text-muted-foreground">
              Text, PDF, and DOCX files are embedded for semantic search. Images and PPTX files are stored without embeddings.
            </p>
          </Card>
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-xl font-semibold">Documents</h2>
            <p className="text-sm text-muted-foreground">MapleWord documents linked to this project.</p>
          </div>

          {projectDocs.length === 0 ? (
            <div className="text-sm text-muted-foreground">No MapleWord documents are linked to this project yet.</div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {projectDocs.map((doc) => (
                <Card key={doc.id} className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="truncate font-medium">{doc.document_title}</div>
                    <span className={`rounded px-2 py-0.5 text-xs ${doc.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {doc.status}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Updated: {new Date(doc.updated_at).toLocaleString()}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openDocument(doc.document_title, doc.file_path)}>
                      Open
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AIProjectDetail;